import React from "react";
import { useNavigate } from "react-router-dom";
import "./FeatureCard.scss";
import { CiStreamOn } from "react-icons/ci";


export interface FeatureCardData {
    id: string;
    title: string;
    description: string;
    backgroundImage: string;
    buttonText?: string;
    buttonRoute?: string;
}

interface FeatureCardProps {
    feature: FeatureCardData;
    onLearnMoreClick?: (route?: string) => void;
    stickyIndex?: number;
    enableStickyStack?: boolean;
}

// Overlay components
const AIHighlightsOverlay: React.FC = () => (
    <div className="feature-card-overlay-elements">
        {/* AI Detection Overlay */}
        <div className="ai-detection-overlay">
            <div className="detection-labels">
                <span>Wicket</span>
                <span>Four</span>
                <span>Six</span>
                <span>Wicket</span>
            </div>
            <div className="detection-divider"></div>
            <div className="detection-dots">
                <div className="detection-dot" style={{ background: "#FF6B6B" }}></div>
                <div className="detection-dot" style={{ background: "#51CF66" }}></div>
                <div className="detection-dot" style={{ background: "#FF6BF7" }}></div>
                <div className="detection-dot" style={{ background: "#FF6B6B" }}></div>
            </div>
        </div>
    </div>
);

const LiveStreamingOverlay: React.FC = () => (
    <div className="feature-card-overlay-elements">
        {/* Live Indicator - Top Right */}
        <div className="live-indicator">
            <CiStreamOn size={24} />
            <span>LIVE</span>
        </div>
    </div>
);

const ScoringOverlay: React.FC = () => {
    const recentBalls = [
        { value: "1", color: "#FFFFFF1A" },
        { value: "4", color: "#00C950" },
        { value: "6", color: "#00C950" },
        { value: "0", color: "#FFFFFF1A" },
        { value: "W", color: "#FB2C36" },
        { value: "2", color: "#FFFFFF1A" }
    ];

    return (
        <div className="feature-card-overlay-elements">
            {/* Score Tracker - Bottom Right */}
            <div className="score-tracker">
                <div className="score-tracker-label">Recent Balls</div>
                <div className="score-tracker-balls">
                    {recentBalls.map((ball, index) => (
                        <div
                            key={index}
                            className="score-ball"
                            style={{ background: ball.color }}
                        >
                            {ball.value}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const FeatureCard: React.FC<FeatureCardProps> = ({
    feature,
    onLearnMoreClick,
    stickyIndex = 0,
    enableStickyStack = false
}) => {
    const navigate = useNavigate();

    const handleLearnMore = () => {
        if (onLearnMoreClick) {
            onLearnMoreClick(feature.buttonRoute);
        } else if (feature.buttonRoute) {
            navigate(feature.buttonRoute);
        }
    };

    const renderOverlay = () => {
        switch (feature.id) {
            case "ai-highlights":
                return <AIHighlightsOverlay />;
            case "live-streaming":
                return <LiveStreamingOverlay />;
            case "effortless-scoring":
                return <ScoringOverlay />;
            default:
                return null;
        }
    };

    return (
        <div
            className={`feature-card ${enableStickyStack ? "feature-card-sticky" : ""}`}
            data-sticky-index={enableStickyStack ? stickyIndex : undefined}
        >
            <div
                className="feature-card-background"
                style={{ backgroundImage: `url(${feature.backgroundImage})` }}
            >
                <div className="feature-card-overlay"></div>
            </div>

            {renderOverlay()}

            <div className="feature-card-content">
                <h3 className="feature-card-title">{feature.title}</h3>
                <p className="feature-card-description">{feature.description}</p>
                <button
                    className="feature-card-button"
                    onClick={handleLearnMore}
                >
                    {feature.buttonText || "Learn More"}
                </button>
            </div>
        </div>
    );
};

export default FeatureCard;

